/**
 * Theme Context for managing user's personalized theme colors
 * Handles theme state management and persistence
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { config } from '../config';

export interface ThemeColors {
  navigationBg: string;
  navigationText: string;
  navigationAccent: string;
  navigationHover: string;
  navigationIconColor: string;
  navigationMenuText: string;
  headerBg: string;
  headerText: string;
  bg: string;
  cardBg: string;
  borderColor: string;
  textColor: string;
  textSecondary: string;
  primary: string;
  secondary: string;
  success: string;
  error: string;
  warning: string;
  info: string;
  gray: string;
}

interface ThemeContextType {
  colors: ThemeColors;
  updateTheme: (newColors: ThemeColors) => Promise<void>;
  loadTheme: () => Promise<void>;
  resetTheme: () => Promise<void>;
  resetNavigationTheme: () => void;
  resetHeaderTheme: () => void;
  isLoading: boolean;
  error: string | null;
}

const DEFAULT_COLORS: ThemeColors = {
  navigationBg: '#ffffff',
  navigationText: '#1f2d3d',
  navigationAccent: '#2c3e50',
  navigationHover: '#e5e7eb',
  navigationIconColor: '#1f2d3d',
  navigationMenuText: '#1f2d3d',
  headerBg: '#ffffff',
  headerText: '#1f2d3d',
  bg: '#f8f9fa',
  cardBg: '#ffffff',
  borderColor: '#e9ecef',
  textColor: '#212529',
  textSecondary: '#6c757d',
  primary: '#1976d2',
  secondary: '#9c27b0',
  success: '#4caf50',
  error: '#d32f2f',
  warning: '#ffa000',
  info: '#0288d1',
  gray: '#bdbdbd',
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [colors, setColors] = useState<ThemeColors>(DEFAULT_COLORS);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Apply theme colors to CSS variables
  const applyThemeColors = (themeColors: ThemeColors) => {
    const root = document.documentElement;
    root.style.setProperty('--navigation-bg', themeColors.navigationBg);
    root.style.setProperty('--navigation-text', themeColors.navigationText);
    root.style.setProperty('--navigation-accent', themeColors.navigationAccent);
    root.style.setProperty('--navigation-hover', themeColors.navigationHover);
    root.style.setProperty('--navigation-icon-color', themeColors.navigationIconColor);
    root.style.setProperty('--navigation-menu-text', themeColors.navigationMenuText);
    root.style.setProperty('--header-bg', themeColors.headerBg);
    root.style.setProperty('--header-text', themeColors.headerText);
    root.style.setProperty('--bg', themeColors.bg);
    root.style.setProperty('--card-bg', themeColors.cardBg);
    root.style.setProperty('--border-color', themeColors.borderColor);
    root.style.setProperty('--text-color', themeColors.textColor);
    root.style.setProperty('--text-secondary', themeColors.textSecondary);
    root.style.setProperty('--primary', themeColors.primary);
    root.style.setProperty('--secondary', themeColors.secondary);
    root.style.setProperty('--success', themeColors.success);
    root.style.setProperty('--error', themeColors.error);
    root.style.setProperty('--warning', themeColors.warning);
    root.style.setProperty('--info', themeColors.info);
    root.style.setProperty('--gray', themeColors.gray);
    
    // Also set navigation-specific variables
    root.style.setProperty('--sidebar-bg', themeColors.navigationBg);
    root.style.setProperty('--sidebar-border', themeColors.borderColor);
    root.style.setProperty('--accent', themeColors.navigationAccent);
    root.style.setProperty('--text', themeColors.navigationText);
    
    // Set data-theme attribute based on background color
    const isDark = themeColors.bg === '#1a202c';
    root.setAttribute('data-theme', isDark ? 'dark' : 'light');
    document.body.setAttribute('data-theme', isDark ? 'dark' : 'light');
  };

  // Update theme via API
  const updateTheme = async (newColors: ThemeColors): Promise<void> => {
    if (!isAuthenticated) {
      throw new Error('User not authenticated');
    }

    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${config.api.baseUrl}/user/theme`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          navigationBg: newColors.navigationBg,
          navigationText: newColors.navigationText,
          navigationAccent: newColors.navigationAccent,
          navigationHover: newColors.navigationHover,
          navigationIconColor: newColors.navigationIconColor,
          navigationMenuText: newColors.navigationMenuText,
          headerBg: newColors.headerBg,
          headerText: newColors.headerText,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to update theme' }));
        throw new Error(errorData.error || 'Failed to update theme');
      }

      // Update local state and apply colors
      setColors(newColors);
      applyThemeColors(newColors);

      console.log('🎨 Theme updated successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      console.error('❌ Error updating theme:', errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Load theme from API
  const loadTheme = useCallback(async (): Promise<void> => {
    if (!isAuthenticated) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${config.api.baseUrl}/user/theme`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to load theme' }));
        throw new Error(errorData.error || 'Failed to load theme');
      }

      const data = await response.json();
      if (data.success && data.theme) {
        const loadedColors: ThemeColors = {
          navigationBg: data.theme.navigationBg || DEFAULT_COLORS.navigationBg,
          navigationText: data.theme.navigationText || DEFAULT_COLORS.navigationText,
          navigationAccent: data.theme.navigationAccent || DEFAULT_COLORS.navigationAccent,
          navigationHover: data.theme.navigationHover || DEFAULT_COLORS.navigationHover,
          navigationIconColor: data.theme.navigationIconColor || DEFAULT_COLORS.navigationIconColor,
          navigationMenuText: data.theme.navigationMenuText || DEFAULT_COLORS.navigationMenuText,
          headerBg: data.theme.headerBg || DEFAULT_COLORS.headerBg,
          headerText: data.theme.headerText || DEFAULT_COLORS.headerText,
          bg: data.theme.bg || DEFAULT_COLORS.bg,
          cardBg: data.theme.cardBg || DEFAULT_COLORS.cardBg,
          borderColor: data.theme.borderColor || DEFAULT_COLORS.borderColor,
          textColor: data.theme.textColor || DEFAULT_COLORS.textColor,
          textSecondary: data.theme.textSecondary || DEFAULT_COLORS.textSecondary,
          primary: data.theme.primary || DEFAULT_COLORS.primary,
          secondary: data.theme.secondary || DEFAULT_COLORS.secondary,
          success: data.theme.success || DEFAULT_COLORS.success,
          error: data.theme.error || DEFAULT_COLORS.error,
          warning: data.theme.warning || DEFAULT_COLORS.warning,
          info: data.theme.info || DEFAULT_COLORS.info,
          gray: data.theme.gray || DEFAULT_COLORS.gray,
        };
        setColors(loadedColors);
        applyThemeColors(loadedColors);
        console.log('🎨 Theme loaded successfully from API');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      console.error('❌ Error loading theme:', errorMessage);
      // В разі помилки використовуємо дефолтну тему
      setColors(DEFAULT_COLORS);
      applyThemeColors(DEFAULT_COLORS);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // Reset to default theme and save to database
  const resetTheme = async (): Promise<void> => {
    setColors(DEFAULT_COLORS);
    applyThemeColors(DEFAULT_COLORS);
    setError(null);

    // Save default theme to database if user is authenticated
    if (isAuthenticated) {
      try {
        await updateTheme(DEFAULT_COLORS);
        console.log('🎨 Theme reset to default and saved to database');
      } catch (err) {
        console.error('❌ Failed to save default theme to database:', err);
      }
    }
  };

  // Reset only navigation theme colors to defaults
  const resetNavigationTheme = (): void => {
    const resetColors = {
      ...colors,
      navigationBg: DEFAULT_COLORS.navigationBg,
      navigationText: DEFAULT_COLORS.navigationText,
      navigationAccent: DEFAULT_COLORS.navigationAccent,
      navigationHover: DEFAULT_COLORS.navigationHover,
      navigationIconColor: DEFAULT_COLORS.navigationIconColor,
      navigationMenuText: DEFAULT_COLORS.navigationMenuText,
    };
    setColors(resetColors);
    applyThemeColors(resetColors);
    setError(null);
    console.log('🎨 Navigation theme reset to default in UI');
  };

  // Reset only header theme colors to defaults
  const resetHeaderTheme = (): void => {
    const resetColors = {
      ...colors,
      headerBg: DEFAULT_COLORS.headerBg,
      headerText: DEFAULT_COLORS.headerText,
    };
    setColors(resetColors);
    applyThemeColors(resetColors);
    setError(null);
    console.log('🎨 Header theme reset to default in UI');
  };

  // Load user's theme when user data is available
  useEffect(() => {
    if (user?.theme) {
      const theme = user.theme as Partial<ThemeColors>;
      const userColors: ThemeColors = {
        navigationBg: theme.navigationBg || DEFAULT_COLORS.navigationBg,
        navigationText: theme.navigationText || DEFAULT_COLORS.navigationText,
        navigationAccent: theme.navigationAccent || DEFAULT_COLORS.navigationAccent,
        navigationHover: theme.navigationHover || DEFAULT_COLORS.navigationHover,
        navigationIconColor: theme.navigationIconColor || DEFAULT_COLORS.navigationIconColor,
        navigationMenuText: theme.navigationMenuText || DEFAULT_COLORS.navigationMenuText,
        headerBg: theme.headerBg || DEFAULT_COLORS.headerBg,
        headerText: theme.headerText || DEFAULT_COLORS.headerText,
        bg: theme.bg || DEFAULT_COLORS.bg,
        cardBg: theme.cardBg || DEFAULT_COLORS.cardBg,
        borderColor: theme.borderColor || DEFAULT_COLORS.borderColor,
        textColor: theme.textColor || DEFAULT_COLORS.textColor,
        textSecondary: theme.textSecondary || DEFAULT_COLORS.textSecondary,
        primary: theme.primary || DEFAULT_COLORS.primary,
        secondary: theme.secondary || DEFAULT_COLORS.secondary,
        success: theme.success || DEFAULT_COLORS.success,
        error: theme.error || DEFAULT_COLORS.error,
        warning: theme.warning || DEFAULT_COLORS.warning,
        info: theme.info || DEFAULT_COLORS.info,
        gray: theme.gray || DEFAULT_COLORS.gray,
      };
      setColors(userColors);
      applyThemeColors(userColors);
      // console.log('🎨 Theme loaded from user data');
    } else if (isAuthenticated) {
      // If user is authenticated but no theme in profile data, load from API
      loadTheme();
    }
  }, [user, isAuthenticated, loadTheme]);

  // Apply default theme on initial load for non-authenticated users
  useEffect(() => {
    if (!isAuthenticated) {
      // Check if user has saved theme preference
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme === 'dark') {
        // Apply dark theme for non-authenticated users
        const darkColors: ThemeColors = {
          ...DEFAULT_COLORS,
          navigationBg: '#1a1a1a',
          navigationText: '#ffffff',
          navigationAccent: '#4a5568',
          navigationHover: '#2d3748',
          navigationIconColor: '#ffffff',
          navigationMenuText: '#ffffff',
          headerBg: '#2d3748',
          headerText: '#ffffff',
          bg: '#1a202c',
          cardBg: '#2d3748',
          borderColor: '#4a5568',
          textColor: '#e2e8f0',
          textSecondary: '#a0aec0',
          primary: '#63b3ed',
          secondary: '#b794f4',
          success: '#68d391',
          error: '#fc8181',
          warning: '#f6ad55',
          info: '#4fd1c5',
          gray: '#718096',
        };
        setColors(darkColors);
        applyThemeColors(darkColors);
      } else {
        setColors(DEFAULT_COLORS);
        applyThemeColors(DEFAULT_COLORS);
      }
    }
  }, [isAuthenticated]);

  const value: ThemeContextType = {
    colors,
    updateTheme,
    loadTheme,
    resetTheme,
    resetNavigationTheme,
    resetHeaderTheme,
    isLoading,
    error,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Hook to use theme context
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
